<template>
  <iRow
    v-if="intent === 'expert'"
    class="full-page"
    width="fill"
    height="fill"
    wrap="nowrap"
    vertical-align="middle"
  >
    <iColumn
      v-if="!shouldWrap"
      class="panel left-panel"
      width="fill"
      height="fill"
      vertical-align="middle"
      align="center"
    >
      <Logo class="absolute-top left" />
      <iRow gap="extraLarge" :max-width="500" width="fill">
        <iText class="extraLarge" variant="light">
          {{ title }}
        </iText>
        <iText class="large" variant="light">
          {{ description }}
        </iText>
      </iRow>
    </iColumn>
    <iColumn
      class="panel right-panel"
      width="fill"
      height="fill"
      vertical-align="middle"
      align="center"
    >
      <iRow gap="extraLarge" :max-width="400" width="fill">
        <component :is="currentUserFlowStep" />
      </iRow>
    </iColumn>
  </iRow>
  <iRow v-else class="auth-form" align="center">
    <component :is="currentUserFlowStep" />
  </iRow>
</template>

<script>
import { mapState, mapWritableState } from "pinia";
import { useUserStore } from "@/stores/user-store";
import EmailForm from "@/components/auth/EmailForm";
import PasswordForm from "@/components/auth/PasswordForm";
import ForgotPasswordForm from "@/components/auth/ForgotPasswordForm";
import InvitedPasswordResetForm from "@/components/auth/InvitedPasswordResetForm";
import SignUpForm from "@/components/auth/SignUpForm";
import AcceptInvitationPasswordForm from "@/components/auth/AcceptInvitationPasswordForm";
import TwoFactorForm from "@/components/auth/TwoFactorForm";
import TwoFactorEnrollForm from "@/components/auth/TwoFactorEnrollForm";
import {
  PASSWORD_FORM,
  SIGN_UP_FORM,
  ACCEPT_INVITATION_PASSWORD_FORM,
  INVITED_PASSWORD_RESET_FORM,
} from "@/constants/login-flow-constants";
import { resolveEmailCheckOutcome } from "%/dynamic-component-editor/helpers/login-flow-steps";
import Logo from "@/components/Logo";
import { useWindowStore } from "@vcl";

export default {
  name: "Auth",
  components: {
    Logo,
    SignUpForm,
    EmailForm,
    PasswordForm,
    ForgotPasswordForm,
    InvitedPasswordResetForm,
    AcceptInvitationPasswordForm,
    TwoFactorForm,
    TwoFactorEnrollForm,
  },
  provide() {
    return {
      intent: this.intent,
    };
  },
  props: {
    intent: {
      type: String,
      required: false,
      default: "",
    },
    title: {
      type: String,
      required: false,
      default: "",
    },
    description: {
      type: String,
      required: false,
      default: "",
    },
  },
  computed: {
    ...mapWritableState(useUserStore, [
      "userAuthEmail",
      "userFirstName",
      "userLastName",
      "userFlowSteps",
      "invitingOrgId",
      "invitationToken",
      "invitedUserId",
      "invitedPublicationId",
      "invitedPublicationName",
      "redirect",
      "signUpNote",
    ]),
    ...mapState(useUserStore, ["currentUserFlowStep"]),
    ...mapState(useWindowStore, [
      "$vssWidth",
    ]),
    shouldWrap() {
      return this.$vssWidth < 768;
    },
  },
  created() {
    useWindowStore().init();
  },
  beforeUnmount() {
    useWindowStore().remove();
  },
  beforeMount() {
    const urlParams = new URLSearchParams(window.location.search);
    this.invitationToken = urlParams.get("token");
    this.invitingOrgId = Number(urlParams.get("inviting_org_id"));
    this.invitedUserId = Number(urlParams.get("user_id"));
    this.invitedPublicationId = Number(urlParams.get("publication_id"));
    this.invitedPublicationName = urlParams.get("publication_name");
    if (urlParams.get("redirect")) {
      this.redirect = urlParams.get("redirect");
    }
    const email = urlParams.get("email");
    const exists = urlParams.get("exists");
    const invitation = urlParams.get("invitation");
    const completeNewsletterAccount = urlParams.get("complete_newsletter_account");
    const requiresReset = urlParams.get("requires_reset");
    if (!email) {
      return;
    }

    window.history.replaceState({}, document.title, window.location.pathname);

    this.userAuthEmail = email;
    this.userFirstName = urlParams.get("first_name");
    this.userLastName = urlParams.get("last_name");

    if (exists === "true") {
      this.userFlowSteps.push(PASSWORD_FORM);
    } else if (invitation === "true") {
      this.userFlowSteps.push(ACCEPT_INVITATION_PASSWORD_FORM);
    } else if (requiresReset === "true") {
      this.userFlowSteps.push(INVITED_PASSWORD_RESET_FORM);
    } else if (completeNewsletterAccount === "true") {
      this.userFlowSteps.push(ACCEPT_INVITATION_PASSWORD_FORM);
    } else {
      this.signUpNote = resolveEmailCheckOutcome({
        exists: false,
        deleted: urlParams.get("deleted") === "true",
        canSignUp: true,
      }).signUpNote;
      this.userFlowSteps.push(SIGN_UP_FORM);
    }
  },
  styleGuide: () => ({
    panelPadding: { "size.spacing": "extraLarge" },
    leftPanelbackground: { "color.font": "branded" },
    background: { "color.background": "paper" },
    largeFontSize: { "size.font": "extraLarge" },
    extraLargeFontSize: { "size.font": "extraExtraLarge" },
  }),
};
</script>


<style lang="scss" scoped>
.full-page {
  height: 100vh !important;
  width: 100%;
  background-color: v-bind('$getStyles.background');

  &:deep(.row) {
    overflow-y: auto !important;
  }
}

.panel {
  padding: v-bind('$getStyles.panelPadding');
  position: relative;

  &.left-panel {
    background: v-bind('$getStyles.leftPanelbackground');
  }
}

.absolute-top {
  position: absolute;
  top: 20px;

  &.right {
    right: 20px;
  }

  &.left {
    left: 20px;
  }
}

.extraLarge {
  font-size: v-bind('$getStyles.extraLargeFontSize');

  &:hover {
    font-size: v-bind('$getStyles.extraLargeFontSize');
  }
}

.large {
  font-size: v-bind('$getStyles.largeFontSize');

  &:hover {
    font-size: v-bind('$getStyles.largeFontSize');
  }
}

.auth-form {
  margin: 0 auto;
  padding: 0 10px 10px 10px;

  position: sticky;
  top: 0;
  z-index: 1;

  div {
    width: 600px;
    margin: 0 auto;
  }
}
</style>
